// Exam script uploads (build doc 05 U9, §5.4; 01 §5.5 "Uploads"; 90 #39/#40).
//
// One file = sign (POST /ingestion/{jobId}/files) → PUT the bytes to the
// signed GCS URL with XHR (upload progress; the signed URL's requiredHeaders
// echoed exactly) → POST …/files/{fileId}/complete. No multipart fallback.
//
// Client checks run before anything is signed (the server re-checks):
//   PDF ≤ 100 MB · JPEG/PNG ≤ 32 MiB · DOCX ≤ 25 MB · ZIP ≤ 200 MB.
//   HEIC/HEIF photos are converted to JPEG in the browser with heic2any
//   (pinned 0.0.4) first, because ingestion refuses HEIC. WebP and every other
//   type are refused.
//
// Failures throw ExamsApiError like service/exams.api.js; nothing resolves
// null or undefined on failure.
import {
  ExamsApiError,
  completeIngestionFile,
  signIngestionFile,
} from './exams.api';

const MB = 1000 * 1000;
const MIB = 1024 * 1024;

export const UPLOAD_LIMITS = Object.freeze({
  pdf: 100 * MB,
  image: 32 * MIB,
  word: 25 * MB,
  zip: 200 * MB,
  // A HEIC original larger than this is refused before conversion (memory).
  heicSource: 100 * MB,
});

const LIMIT_COPY = Object.freeze({
  pdf: 'PDFs can be up to 100 MB.',
  image: 'Photos can be up to 32 MB.',
  word: 'Word files can be up to 25 MB.',
  zip: 'ZIP files can be up to 200 MB.',
});

export const UPLOAD_COPY = Object.freeze({
  unsupported:
    "This file type isn't supported. Use PDF, JPG, PNG, HEIC, Word (.docx) or ZIP.",
  tooLarge: (kind) =>
    `This file is too large. ${LIMIT_COPY[kind] || ''}`.trim(),
  heicFailed: "We couldn't convert this photo. Save it as a JPG and try again.",
  failed: "This file didn't upload. Try again.",
});

export const CONTENT_TYPES = Object.freeze({
  pdf: 'application/pdf',
  jpeg: 'image/jpeg',
  png: 'image/png',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  zip: 'application/zip',
});

// Accept attribute for the file input (WebP deliberately absent).
export const UPLOAD_ACCEPT =
  '.pdf,.jpg,.jpeg,.png,.heic,.heif,.docx,.zip,application/pdf,image/jpeg,image/png,image/heic,image/heif,application/zip';

const extensionOf = (name = '') => {
  const match = /\.([a-z0-9]+)$/i.exec(String(name));
  return match ? match[1].toLowerCase() : '';
};

/**
 * What a file is, from its extension first and its MIME type second:
 * {kind: pdf|image|word|zip|heic|null, contentType}.
 */
export const classifyFile = (file) => {
  const ext = extensionOf(file?.name);
  const type = String(file?.type || '').toLowerCase();
  if (ext === 'pdf' || type === 'application/pdf') {
    return { kind: 'pdf', contentType: CONTENT_TYPES.pdf };
  }
  if (['jpg', 'jpeg'].includes(ext) || type === 'image/jpeg') {
    return { kind: 'image', contentType: CONTENT_TYPES.jpeg };
  }
  if (ext === 'png' || type === 'image/png') {
    return { kind: 'image', contentType: CONTENT_TYPES.png };
  }
  if (
    ['heic', 'heif'].includes(ext) ||
    type === 'image/heic' ||
    type === 'image/heif'
  ) {
    return { kind: 'heic', contentType: CONTENT_TYPES.jpeg };
  }
  if (ext === 'docx' || type === CONTENT_TYPES.docx) {
    return { kind: 'word', contentType: CONTENT_TYPES.docx };
  }
  if (
    ext === 'zip' ||
    type === 'application/zip' ||
    type === 'application/x-zip-compressed'
  ) {
    return { kind: 'zip', contentType: CONTENT_TYPES.zip };
  }
  return { kind: null, contentType: type || null };
};

const refusal = (code, message) =>
  new ExamsApiError(message, {
    status: code === 'FILE_TOO_LARGE' ? 413 : 422,
    code,
  });

/**
 * The client-side check for one file, before any conversion.
 * Returns null when it may be uploaded, else an ExamsApiError
 * (UNSUPPORTED_FILE_TYPE | FILE_TOO_LARGE).
 */
export const checkUploadFile = (file) => {
  const { kind } = classifyFile(file);
  if (!kind) return refusal('UNSUPPORTED_FILE_TYPE', UPLOAD_COPY.unsupported);
  const size = Number(file?.size) || 0;
  if (kind === 'heic') {
    return size > UPLOAD_LIMITS.heicSource
      ? refusal('FILE_TOO_LARGE', UPLOAD_COPY.tooLarge('image'))
      : null;
  }
  return size > UPLOAD_LIMITS[kind]
    ? refusal('FILE_TOO_LARGE', UPLOAD_COPY.tooLarge(kind))
    : null;
};

const jpegName = (name) =>
  /\.(heic|heif)$/i.test(name)
    ? name.replace(/\.(heic|heif)$/i, '.jpg')
    : `${name}.jpg`;

/** HEIC/HEIF → JPEG File (heic2any, loaded only when a HEIC file arrives). */
export const convertHeicToJpeg = async (file) => {
  let output;
  try {
    const module = await import('heic2any');
    const heic2any = module.default || module;
    output = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
  } catch (error) {
    throw new ExamsApiError(UPLOAD_COPY.heicFailed, {
      code: 'UNSUPPORTED_FILE_TYPE',
      status: 422,
    });
  }
  const blob = Array.isArray(output) ? output[0] : output;
  if (!blob) {
    throw new ExamsApiError(UPLOAD_COPY.heicFailed, {
      code: 'UNSUPPORTED_FILE_TYPE',
      status: 422,
    });
  }
  return new File([blob], jpegName(file.name || 'photo.heic'), {
    type: CONTENT_TYPES.jpeg,
    lastModified: file.lastModified || Date.now(),
  });
};

/**
 * The file as it will be uploaded: HEIC converted to JPEG, then checked
 * against the caps. Resolves {file, kind, contentType}; throws ExamsApiError.
 */
export const prepareUploadFile = async (file) => {
  const refused = checkUploadFile(file);
  if (refused) throw refused;
  const classified = classifyFile(file);
  if (classified.kind !== 'heic') return { file, ...classified };
  const jpeg = await convertHeicToJpeg(file);
  if (jpeg.size > UPLOAD_LIMITS.image) {
    throw refusal('FILE_TOO_LARGE', UPLOAD_COPY.tooLarge('image'));
  }
  return { file: jpeg, kind: 'image', contentType: CONTENT_TYPES.jpeg };
};

/**
 * PUT bytes to a signed URL with XHR so progress is reported.
 * @param {string} url
 * @param {Blob} body
 * @param {object} headers  the signed URL's requiredHeaders, echoed exactly
 * @param {{onProgress?: (pct:number) => void, signal?: AbortSignal}} [opts]
 */
export const putWithProgress = (
  url,
  body,
  headers = {},
  { onProgress, signal } = {}
) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new ExamsApiError('Upload cancelled', { code: 'ABORTED' }));
      return;
    }
    const xhr = new XMLHttpRequest();
    const onAbortSignal = () => xhr.abort();
    const cleanup = () => signal?.removeEventListener?.('abort', onAbortSignal);
    xhr.open('PUT', url);
    Object.entries(headers || {}).forEach(([name, value]) => {
      xhr.setRequestHeader(name, String(value));
    });
    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0) {
          onProgress(
            Math.min(100, Math.round((event.loaded / event.total) * 100))
          );
        }
      };
    }
    xhr.onload = () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        resolve({ ok: true, status: xhr.status });
      } else {
        reject(
          new ExamsApiError(`Upload failed (${xhr.status})`, {
            status: xhr.status,
            code: 'UPLOAD_FAILED',
          })
        );
      }
    };
    xhr.onerror = () => {
      cleanup();
      reject(new ExamsApiError('Upload failed', { code: 'NETWORK' }));
    };
    xhr.onabort = () => {
      cleanup();
      reject(new ExamsApiError('Upload cancelled', { code: 'ABORTED' }));
    };
    signal?.addEventListener?.('abort', onAbortSignal);
    xhr.send(body);
  });

/**
 * Upload one file into an exam's ingestion job.
 * @param {string} jobId
 * @param {File} rawFile
 * @param {object} [opts]
 * @param {(pct:number) => void} [opts.onProgress]
 * @param {(fileId:string) => void} [opts.onSigned]  called once the server has a file row (for ✕)
 * @param {(file:File) => void} [opts.onPrepared]    the file actually sent (HEIC → JPEG)
 * @param {AbortSignal} [opts.signal]
 * @returns the completed file view ({fileId, name, pageCountEstimate, …})
 */
export const uploadIngestionFile = async (
  jobId,
  rawFile,
  { onProgress, onSigned, onPrepared, signal } = {}
) => {
  const { file, contentType } = await prepareUploadFile(rawFile);
  onPrepared?.(file);
  if (signal?.aborted) {
    throw new ExamsApiError('Upload cancelled', { code: 'ABORTED' });
  }
  const signed = await signIngestionFile(jobId, {
    fileName: file.name,
    contentType,
    sizeBytes: file.size,
  });
  if (!signed?.fileId || !signed?.uploadUrl) {
    throw new ExamsApiError(UPLOAD_COPY.failed, { code: 'EMPTY_RESPONSE' });
  }
  onSigned?.(signed.fileId);
  await putWithProgress(signed.uploadUrl, file, signed.requiredHeaders, {
    onProgress,
    signal,
  });
  const completed = await completeIngestionFile(jobId, signed.fileId);
  return { fileId: signed.fileId, ...completed };
};
